<template>
  <DynamicErrorDialog
    :is-open="dialog.state.error.isOpen"
    :title="dialog.state.error.title"
    :message="dialog.state.error.message"
    :primary-button-text="dialog.state.error.primaryButtonText"
    :show-primary-button="!!(dialog.state.error.primaryButtonText && dialog.state.error.onPrimaryAction)"
    @primary-action="handleErrorPrimaryAction"
    secondary-button-text="Close"
    @secondary-action="handleErrorClose"
    :redirect-url="currentUrl"
    :auto-close-delay="dialog.state.error.time"
    :preserve-scroll="true"
    :count-down-message="dialog.state.error.countDownMessage"
    @close="handleErrorClose"
  />

  <DynamicSuccessDialog 
    :is-open="dialog.state.success.isOpen"
    :title="dialog.state.success.title"
    :message="dialog.state.success.message"
    :primary-button-text="dialog.state.success.primaryButtonText"
    :show-primary-button="!!(dialog.state.success.primaryButtonText && dialog.state.success.onPrimaryAction)"
    @primary-action="handleSuccessPrimaryAction"
    secondary-button-text="Close"
    @secondary-action="handleSuccessClose"
    :redirect-url="currentUrl"
    :auto-close-delay="dialog.state.success.time"
    :preserve-scroll="true"
    :count-down-message="dialog.state.success.countDownMessage"
    @close="handleSuccessClose"
  />

  <LoadingDialog
    :is-open="dialog.state.loading.isOpen"
    :hide-close-button="true"
    :disable-close-modal-click-outside="true"
    :title="dialog.state.loading.title"
    :message="dialog.state.loading.message"
    @update:is-open="handleLoadingClose"
  />

  <DynamicConfirmationDialog
    :is-open="dialog.state.confirmation.isOpen"
    :title="dialog.state.confirmation.title"
    :message="dialog.state.confirmation.message"
    :primary-button-text="dialog.state.confirmation.primaryButtonText"
    :secondary-button-text="dialog.state.confirmation.secondaryButtonText"
    :on-primary-action="dialog.state.confirmation.onPrimaryAction"
    :on-secondary-action="dialog.state.confirmation.onSecondaryAction"
    @primary-action="handleConfirmationPrimaryAction"
    @secondary-action="handleConfirmationSecondaryAction"
  />
</template>

<script setup lang="ts">
import DynamicErrorDialog from './DynamicErrorDialog.vue'
import DynamicSuccessDialog from './DynamicSuccessDialog.vue'
import DynamicConfirmationDialog from './DynamicConfirmationDialog.vue'
import LoadingDialog from './LoadingDialog.vue'
import { useDialog } from '@/composables/useDialog'
import { onBeforeUnmount, onMounted, watch, computed } from 'vue'
import { useRouter } from 'vue-router'

const dialog = useDialog()
const router = useRouter()

// Get current URL for redirect (redirects to same page, effectively doing nothing)
const currentUrl = computed(() => router.currentRoute.value.fullPath)

// Watch for route changes
watch(() => router.currentRoute.value.fullPath, () => {
  dialog.close()
})

// Close dialogs when component is unmounted
onBeforeUnmount(() => {
  dialog.close()
})

// Close any lingering dialogs on mount
onMounted(() => {
  dialog.close()
})

const handleClose = () => {
  dialog.close()
}

const handleRetry = () => {
  dialog.close()
  try {
    window.dispatchEvent(new CustomEvent('dialog-retry'))
  } catch (error) {
    console.warn('Failed to dispatch dialog-retry event:', error)
  }
}

const handleErrorPrimaryAction = () => {
  if (dialog.state.error.onPrimaryAction) {
    dialog.state.error.onPrimaryAction()
  }
  dialog.close()
}

const handleSuccessPrimaryAction = () => {
  if (dialog.state.success.onPrimaryAction) {
    dialog.state.success.onPrimaryAction()
  }
  dialog.close()
}

const handleErrorClose = () => {
  dialog.close()
}

const handleSuccessClose = () => {
  dialog.close()
}

const handleLoadingClose = () => {
  dialog.close()
}

const handleConfirmationPrimaryAction = () => {
  if (dialog.state.confirmation.onPrimaryAction) {
    dialog.state.confirmation.onPrimaryAction()
  }
  dialog.close()
}

const handleConfirmationSecondaryAction = () => {
  if (dialog.state.confirmation.onSecondaryAction) {
    dialog.state.confirmation.onSecondaryAction()
  }
  dialog.close()
}
</script> 